/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Stack, Switch, Typography } from '@mui/material'
import { AlertCircleIcon } from 'lucide-react'
import { useState, useMemo } from 'react'

import { LanguageSelectBulkImport } from '../../../shared/LanguageSelectBulkImport'
import { EvaluationCriteriaRespondBulkImport } from '../../../shared/EvaluationCriteriaRespondBulkImport'

import { BulkEditType } from './BulkEditType'

import { useBulkImportStore } from '@/components/BulkImportModal/hooks/useBulkImportStore'
import PointsAdjust from '@/components/PointsAdjust'
import { ToggleButtonGradingMethodMCCard } from '@/components/ToggleButtonGradingMethodMCCard'
import { GradingMethodSelectCards } from '@/components/GradingMethodSelect'
import { bulkImportStoreActions } from '@/components/BulkImportModal/store/bulk-import.store'
import { useDefaultRubricId } from '@/hooks/useDefaultRubricId'
import { useRubric } from '@/hooks/useRubrics'
import RubricCard from '@/components/RubricCard'
import RubricDialog from '@/components/RubricBuilder'
import { ReactComponent as UnsplashIcon } from '@/assets/icons/unsplash-black.svg'
import GoogleIconColored from '@/assets/icons/google-icon-colored.png'
import FeedbackLanguageSelector from '@/components/FeedbackSelector/FeedbackLanguageSelector'
import { LineChart } from '@/views/NewBuilder/components/IconsBuilder'
import { LeniencySelect } from '@/views/NewBuilder/components/LeniencySelect'
import { FeedbackCheckboxes } from '@/components/CardFeedbackTypesCheckboxes'
import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { CheckboxUI } from '@/components/CheckboxUI'
// Imports for Standards Based Grading in Bulk Edit Modal
import StandardSelectionDropdown from '@/components/StandardSelectionDropdown'
import TargetProficiencyLevelSelectionDropdown from '@/components/TargetProficiencyLevelSelectionDropdown'
import { useProficiencyStandards, type ProficiencyStandard } from '@/hooks/useProficiencyStandards'

export function ContentBulkImportEditModal({
  dataToUpdate,
  handleChangeUpdates,
  handleChangeField,
}: {
  dataToUpdate: {
    field: string
    updates: any
  }
  handleChangeUpdates: (updates: any) => void
  handleChangeField: (field: string) => void
}) {
  const bulkType = useBulkImportStore(store => store.modalBulkType)
  const settings = useBulkImportStore(store => store.settings)

  const isBulkEdit = bulkType === 'bulk_edit'
  const isBulkDelete = bulkType === 'bulk_delete'
  const isGradingMethod = bulkType === 'select_grading_method'
  const isGenerateTranslations = bulkType === 'generate_translations'

  const isUnsplash = dataToUpdate.updates === 'unsplash'
  const isGoogle = dataToUpdate.updates === 'google'

  return (
    <Stack
      sx={{
        gap: '12px',
      }}
    >
      {isBulkDelete ? (
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#344054',
            paddingX: '12px',
          }}
        >
          Are you sure you want to delete these cards?
        </Typography>
      ) : null}
      {isBulkEdit ? <BulkEditType field={dataToUpdate.field} setField={handleChangeField} /> : null}
      {dataToUpdate.field === 'language' ? (
        <Stack
          sx={{
            gap: '6px',
            paddingX: '12px',
          }}
        >
          <LanguageSelectBulkImport
            handleLanguage={language => {
              handleChangeUpdates(language)
            }}
            label="Target Language"
            language={dataToUpdate.updates || settings.language}
          />
        </Stack>
      ) : null}

      {dataToUpdate.field === 'points' ? (
        <Stack
          sx={{
            gap: '6px',
            paddingX: '12px',
          }}
        >
          <Typography fontWeight="semibold">Points per page</Typography>
          <PointsAdjust
            points={dataToUpdate.updates || settings.points}
            // @ts-ignore
            setPoints={(points: any) => {
              handleChangeUpdates(points)
            }}
            sx={{
              maxWidth: '50px',
            }}
          />
        </Stack>
      ) : null}

      {dataToUpdate.field === 'repeat' ? (
        <Stack
          sx={{
            gap: '6px',
            paddingX: '12px',
          }}
        >
          <Typography fontWeight="semibold">Repeat times</Typography>
          <PointsAdjust
            points={dataToUpdate.updates || 1}
            // @ts-ignore
            setPoints={(repeat: any) => {
              handleChangeUpdates(repeat)
            }}
            sx={{
              maxWidth: '50px',
            }}
          />
        </Stack>
      ) : null}

      {dataToUpdate.field === 'imageUrl' ? (
        <Stack
          sx={{
            bgcolor: '#FFFAEB',
            padding: '12px',
            borderRadius: '16px',
            gap: '6px',
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#FEF0C7',
              width: 'fit-content',
              padding: '8px',
              borderRadius: '50%',
            }}
          >
            <AlertCircleIcon color="#F79009" size={18} />
          </Stack>
          <Typography fontWeight="semibold">Remove selected images?</Typography>
          <Typography>
            This action will delete the selected images from your cards. This change can't be undone.
          </Typography>
        </Stack>
      ) : null}

      {dataToUpdate.field === 'MCQType' ? (
        <Stack
          sx={{
            gap: '6px',
          }}
        >
          <Typography fontWeight="semibold">Grading Method</Typography>
          <ToggleButtonGradingMethodMCCard
            value={dataToUpdate.updates || settings.selectType}
            onChange={handleChangeUpdates}
          />
        </Stack>
      ) : null}

      {dataToUpdate.field === 'feedback_types' ? (
        <Stack
          sx={{
            gap: '6px',
            paddingX: '12px',
          }}
        >
          <Typography fontWeight="semibold">Feedback Types</Typography>
          <FeedbackCheckboxes
            showIcon
            showTooltip
            feedbacks={(dataToUpdate.updates ?? []) || settings.feedback}
            handleFeedbacks={handleChangeUpdates}
            language={settings.language}
          />
        </Stack>
      ) : null}

      {dataToUpdate.field === 'allowTTS' ? (
        <Stack sx={{ gap: '6px', paddingX: '10px', justifyContent: 'space-between', flexDirection: 'row' }}>
          <Stack>
            <Typography fontWeight="semibold">Text to speech</Typography>
            <Typography color="#475467" variant="xs">
              Let students hear the prompt
            </Typography>
          </Stack>

          <Switch
            value={dataToUpdate.updates || false}
            onChange={(e, checked) => {
              handleChangeUpdates(checked)
            }}
          />
        </Stack>
      ) : null}
      {dataToUpdate.field === 'difficulty' ? (
        <Stack
          sx={{
            gap: '6px',
            paddingX: '12px',
          }}
        >
          <Typography fontWeight="semibold">Pronunciation Leniency</Typography>
          <Typography color="#344054" variant="xs">
            More leniency means more forgiving pronunciation grading.
          </Typography>
          <LeniencySelect
            difficulty={dataToUpdate.updates || settings.grading.leniency}
            icon={
              <Stack
                sx={{
                  bgcolor: '#86CB3C',
                  padding: '6px',
                  borderRadius: '12px',
                  border: '1.5px solid #F4F3FF',
                }}
              >
                <LineChart />
              </Stack>
            }
            onChange={value => {
              handleChangeUpdates(value)
            }}
          />
        </Stack>
      ) : null}

      {dataToUpdate.field === 'grading_method' || isGradingMethod ? <GradingMethodSection /> : null}
      {dataToUpdate.field === 'feedback_language' ? (
        <Stack
          sx={{
            gap: '6px',
            paddingX: '12px',
          }}
        >
          <FeedbackLanguageSelector
            selectSx={{
              width: '100%',
            }}
            targetLanguage={settings.language}
            value={dataToUpdate.updates || settings.feedbackLanguage}
            onChange={value => {
              handleChangeUpdates(value)
            }}
          />
        </Stack>
      ) : null}

      {isGenerateTranslations ? (
        <Stack sx={{ gap: '6px' }}>
          <LanguageSelectBulkImport
            handleLanguage={language => {
              handleChangeUpdates(language)
            }}
            label="Select Target Language"
            language={settings.language}
          />
        </Stack>
      ) : null}

      {bulkType === 'add_images' ? (
        <Stack sx={{ gap: '12px' }}>
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
            <CheckboxUI
              checked={isUnsplash}
              handlePointerDown={() => {
                handleChangeUpdates('unsplash')
              }}
            />
            <UnsplashIcon
              height={16}
              style={{
                marginInline: '4px',
              }}
              width={16}
            />
            <Typography fontWeight="semibold">Add from Unsplash</Typography>
          </Stack>

          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
            <CheckboxUI
              checked={isGoogle}
              handlePointerDown={() => {
                handleChangeUpdates('google')
              }}
            />
            <img alt="Google" height={24} src={GoogleIconColored} width={24} />
            <Typography fontWeight="semibold">Add from Google</Typography>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  )
}

function GradingMethodSection() {
  const [isRubricDialogOpen, setIsRubricDialogOpen] = useState(false)
  const settings = useBulkImportStore(store => store.settings)
  const gradingMethodSettings = settings.grading.method
  const selectedCards = useBulkImportStore(store => store.selectedCards)

  const defaultRubricId = useDefaultRubricId()
  const rubricToUse = settings.grading.rubricId || defaultRubricId

  const { rubric } = useRubric({
    id: rubricToUse,
    enabled: gradingMethodSettings === 'rubric',
  })

  // --- Proficiency Standards Hook for Bulk Edit Modal ---
  const {
    data: proficiencyStandards,
    isLoading: isLoadingStandards,
    error: standardsError,
  } = useProficiencyStandards({
    language: settings.language,
    enabled: gradingMethodSettings === 'standards_based',
  })

  const selectedStandard = useMemo<ProficiencyStandard | undefined>(() => {
    if (gradingMethodSettings !== 'standards_based' || !proficiencyStandards || !settings.grading.standardId) return undefined
    return proficiencyStandards.find(s => s.id === settings.grading.standardId)
  }, [gradingMethodSettings, proficiencyStandards, settings.grading.standardId])
  // -----------------------------------------------------

  const handleStandardChange = (newStandardId: string) => {
    // get first level of the new standard  
    const firstLevel = proficiencyStandards?.find(s => s.id === newStandardId)?.levels[0]
    if (firstLevel) {
      bulkImportStoreActions.updateGrading({ 
        standardId: newStandardId, 
        target_proficiency_level: firstLevel.id
      })
    }
  }

  const handleTargetLevelChange = (newLevelId: string) => {
    bulkImportStoreActions.updateGrading({ target_proficiency_level: newLevelId })
  }

  return (
    <Stack sx={{ gap: '12px' }}>
      <Typography fontWeight="semibold">Grading Method</Typography>

      <GradingMethodSelectCards
        value={settings.grading.method}
        onChange={gradingMethod => {
          bulkImportStoreActions.updateGrading({
            method: gradingMethod,
            standardId: gradingMethod === 'standards_based' ? settings.grading.standardId : undefined,
            target_proficiency_level: gradingMethod === 'standards_based' ? settings.grading.target_proficiency_level : undefined,
            rubricId: gradingMethod === 'rubric' ? settings.grading.rubricId : undefined,
          })
          if (gradingMethod === 'rubric') {
            bulkImportStoreActions.updateSettings({
              points: rubric?.total_points,
            })
          }
          if (selectedCards.length > 0) {
            const updateInstructions = selectedCards.map(cardId => ({
              cardId,
              fieldToUpdate: 'autoGrade' as keyof CardActivityWithId,
              updates: gradingMethod !== 'manual',
            }))
            bulkImportStoreActions.handleUpdateFieldTable(updateInstructions)
          }
        }}
      />

      <RubricDialog
        isOnlyOneSave
        open={isRubricDialogOpen}
        rubricId={rubricToUse}
        onClose={() => {
          setIsRubricDialogOpen(false)
        }}
        onSave={(newRubricId, selectedRubric) => {
          bulkImportStoreActions.updateGrading({
            rubricId: newRubricId as string,
          })
          bulkImportStoreActions.updateSettings({
            points: selectedRubric?.total_points,
          })
          setIsRubricDialogOpen(false)
        }}
      />

      {gradingMethodSettings === 'rubric' ? (
        <RubricCard
          description={rubric?.description}
          name={rubric?.name}
          total_points={rubric?.total_points}
          onClick={() => {
            setIsRubricDialogOpen(true)
          }}
        />
      ) : null}

      {gradingMethodSettings === 'manual' ? (
        <Typography>
          You&apos;ll review and score each response yourself. Ideal when you want full control over how students are
          evaluated.
        </Typography>
      ) : null}

      {gradingMethodSettings === 'simple' ? (
        <EvaluationCriteriaRespondBulkImport
          type="dropdown"
          aiFocus={settings.grading.aiFocus}
          generateWithAI={settings.grading.gradingCriteriaWithAI}
          performanceExpectations={settings.grading.performanceExpectations}
          studentLevel={settings.grading.studentLevel}
          onChangeAIFocus={aiFocus => {
            bulkImportStoreActions.updateGrading({
              aiFocus: aiFocus as 'language' | 'correctness' | 'both',
            })
          }}
          onChangePerformanceExpectations={performanceExpectations => {
            bulkImportStoreActions.updateGrading({
              performanceExpectations: performanceExpectations,
            })
          }}
          onChangeStudentLevel={studentLevel => {
            bulkImportStoreActions.updateGrading({
              studentLevel: studentLevel,
            })
          }}
          onSwitchGenerateWithAI={value => {
            bulkImportStoreActions.updateGrading({
              gradingCriteriaWithAI: value,
            })
          }}
        />
      ) : null}

      {gradingMethodSettings === 'standards_based' && (
        <Stack gap="12px" mt={1}>
          {isLoadingStandards && <Typography variant="body2">Loading standards...</Typography>}
          {standardsError && (
            <Typography color="error" variant="body2">
              Error loading standards. Please ensure language is set or try again.
            </Typography>
          )}
          {!isLoadingStandards && !standardsError && proficiencyStandards && (
            <>
              <StandardSelectionDropdown
                standards={proficiencyStandards}
                selectedStandardId={settings.grading.standardId || ''}
                onChange={handleStandardChange}
                disabled={!proficiencyStandards || proficiencyStandards.length === 0}
                label="Proficiency Standard"
                error={!proficiencyStandards || proficiencyStandards.length === 0}
                helperText={(!proficiencyStandards || proficiencyStandards.length === 0) ? 'No standards available for the selected language.' : (standardsError ? 'Error loading standards' : undefined)}
              />
              {settings.grading.standardId && selectedStandard && (
                <TargetProficiencyLevelSelectionDropdown
                  levels={selectedStandard.levels || []}
                  selectedLevelId={settings.grading.target_proficiency_level || ''}
                  onChange={handleTargetLevelChange}
                  disabled={!selectedStandard.levels || selectedStandard.levels.length === 0}
                  label="Target Proficiency Level"
                  standardName={selectedStandard.name}
                  error={!selectedStandard.levels || selectedStandard.levels.length === 0}
                  helperText={(!selectedStandard.levels || selectedStandard.levels.length === 0) ? `No levels available for ${selectedStandard.name}.` : undefined}
                />
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  )
}
